// P2b tanda 2 — construye el cuerpo a WooCommerce (contrato §3.7) y las reglas
// de guardado/publicación. Función pura: solo incluye lo que el formulario
// gestiona y tiene cargado; omite el resto para no pisarlo. En esta tanda NUNCA
// toca images/brands/tags/cross_sell/variaciones.

import { TIPOS_PRODUCTO_DEFAULT, arquetipoDeTipo, tipoActivo } from './tiposProducto';
import { normalizarTabla } from './tablaMedidas';
import { mergeTagsColeccion } from './tagsColeccion';
import { bogotaLocalAGmtIso } from './colTimeUtils';
import { payloadMasOpcionesProducto } from './masOpciones';

const num = (v) => String(v == null ? '' : v).trim();
const has = (v) => num(v) !== '';

// Tanda 3g (decisión de Miguel, 14-sep-2026): la regla de categorías
// (includeCategories) generalizada a los campos de TEXTO. En EDICIÓN
// (soloTocados=true), sku/GTIN/descripciones/Yoast viajan SOLO si el usuario
// los modificó (camposTocados) — un vacío TOCADO viaja y limpia a propósito;
// lo no tocado se OMITE y conserva. En CREACIÓN (sin soloTocados) todo viaja
// como siempre. Cierra la clase del incidente #10902: un form que perdió el
// sku por la vía que sea ya no puede vaciarlo en Woo, porque sin tocarlo el
// campo ni siquiera viaja.
export function formToWooPayload(form, { status, includeCategories = true, includeImages = false, soloTocados = false, camposTocados = null, attributes = null, tipos = TIPOS_PRODUCTO_DEFAULT } = {}) {
  const f = form || {};
  const enviar = (k) => !soloTocados || !!(camposTocados && camposTocados[k]);
  const payload = {
    name: num(f.name),
    type: f.type || 'simple',
    // P3-12: `backorders` dejó de forzarse a 'no'. Ahora es CONDICIONAL al tipo (lo agrega
    // payloadMasOpcionesProducto para el producto simple cuando el tipo permite reservas y se tocó);
    // el variable las lleva por variación. Un producto de un tipo sin reservas no lo manda → conserva.
  };
  // P16 (Programados): el `status` viaja SOLO cuando el usuario lo tocó —
  // en CREACIÓN siempre (producto nuevo necesita estado); en EDICIÓN solo si
  // cambió el estado/programación. Así editar colección, foto o precio de un
  // producto PROGRAMADO (future) NO pisa su status ni su fecha de publicación.
  if (enviar('estado')) {
    const st = status || 'draft';
    payload.status = st;
    // «Programada» (future): la fecha/hora de Bogotá va como date_created_gmt (GMT).
    if (st === 'future' && has(f.fechaProgramada)) {
      const gmt = bogotaLocalAGmtIso(f.fechaProgramada);
      if (gmt) payload.date_created_gmt = gmt;
    }
  }
  if (enviar('description')) payload.description = f.description || '';
  if (enviar('shortDescription')) payload.short_description = f.shortDescription || '';
  // Tanda 3b (defecto 1): el SKU no viajaba y el producto quedaba sin SKU en
  // WordPress. En creación va SIEMPRE (vacío incluido); en edición, solo tocado.
  if (enviar('sku')) payload.sku = num(f.sku);

  // GTIN: en creación, solo con valor (como siempre); en edición, si se tocó —
  // incluido vaciarlo a propósito (viaja "" y limpia).
  if (soloTocados ? enviar('gtin') : has(f.gtin)) payload.global_unique_id = num(f.gtin);
  if (has(f.weight)) payload.weight = num(f.weight);
  if (has(f.length) || has(f.width) || has(f.height)) {
    payload.dimensions = { length: num(f.length), width: num(f.width), height: num(f.height) };
  }

  // Categorías (tanda 2b): solo si el usuario las tocó (includeCategories). Se
  // mandan TODAS las marcadas (categoryIds = árbol con casillas). Fallback legado
  // a parentCatId/subCatId cuando no hay categoryIds (compatibilidad).
  if (includeCategories) {
    let cats = [];
    if (Array.isArray(f.categoryIds) && f.categoryIds.length) {
      cats = f.categoryIds.map((id) => ({ id: Number(id) }));
    } else {
      if (has(f.parentCatId)) cats.push({ id: Number(f.parentCatId) });
      if (has(f.subCatId)) cats.push({ id: Number(f.subCatId) });
    }
    if (cats.length) payload.categories = cats;
  }

  // Imágenes (tanda 3): solo si el usuario las tocó. SIEMPRE por id, nunca por src.
  if (includeImages) {
    payload.images = (f.images || []).map((i) => ({ id: i.id })).filter((i) => i.id != null);
  }

  // Yoast: en creación, solo las claves con valor; en edición, las TOCADAS
  // (un value "" tocado viaja y limpia la meta). Si ninguna, se omite meta_data.
  const meta = [];
  if (soloTocados ? enviar('seoTitle') : has(f.seoTitle)) meta.push({ key: '_yoast_wpseo_title', value: num(f.seoTitle) });
  if (soloTocados ? enviar('seoMetadesc') : has(f.seoMetadesc)) meta.push({ key: '_yoast_wpseo_metadesc', value: num(f.seoMetadesc) });
  // Tanda 3h (ítem 1): el Tipo de producto persiste como _vitrina_arquetipo
  // (contrato v0.7.155). Tanda 4c: el arquetipo sale del TIPO CONFIGURADO
  // (prenda | cosmetico); un tipo con arquetipo "ninguno" manda null y BORRA la
  // meta. Creación: siempre; edición: solo si el usuario lo tocó (el deducido/
  // sugerido no viaja — no se escribe en Woo una deducción que el dueño no eligió).
  if (enviar('productKind')) {
    meta.push({ key: '_vitrina_arquetipo', value: arquetipoDeTipo(tipos, f.productKind) });
  }
  // Tanda 4: contenido del PADRE — objeto {valor,unidad}; vacío TOCADO = null
  // (borra la meta, contrato v0.7.155). En creación solo viaja con valor.
  const hayContenido = has(f.contenidoValor);
  if (soloTocados ? (enviar('contenidoValor') || enviar('contenidoUnidad')) : hayContenido) {
    meta.push({ key: '_vitrina_contenido', value: hayContenido ? { valor: num(f.contenidoValor), unidad: f.contenidoUnidad || 'ml' } : null });
  }
  // Tanda 5: guía de tallas (contrato v0.7.156) — SOLO en edición (el bloque
  // de un producto nuevo dice "se asigna después de guardar") y SOLO tocadas.
  // _vitrina_guia_id: int; vacío tocado = null (borra, vuelve a la categoría).
  // _vitrina_medidas: objeto {columnas,filas} NORMALIZADO con las reglas del
  // theme; null borra la tabla propia.
  if (soloTocados && enviar('guiaId')) {
    meta.push({ key: '_vitrina_guia_id', value: has(f.guiaId) ? Number(f.guiaId) : null });
  }
  if (soloTocados && enviar('medidas')) {
    meta.push({ key: '_vitrina_medidas', value: f.medidas ? normalizarTabla(f.medidas) : null });
  }
  // Tanda 7: "Combínalo con" (cross_sell_ids). En edición solo TOCADO — un []
  // tocado viaja y limpia la lista; sin tocar se omite y conserva. En creación
  // solo con contenido (como el resto de los opcionales).
  if (soloTocados ? enviar('crossSellIds') : (Array.isArray(f.crossSellIds) && f.crossSellIds.length > 0)) {
    payload.cross_sell_ids = (f.crossSellIds || []).map(Number);
  }
  // P3-4: marca (taxonomía brands) — una. En edición solo TOCADA (un '' tocado
  // viaja como [] y la quita); en creación solo con valor.
  if (soloTocados ? enviar('brandId') : has(f.brandId)) {
    payload.brands = f.brandId ? [{ id: Number(f.brandId) }] : [];
  }
  // P3-4: colecciones (tags coleccion-*) — varias. Se escribe la lista COMPLETA
  // de tags PRESERVANDO las normales (Sale) con mergeTagsColeccion. En edición
  // solo TOCADA; en creación solo si hay colecciones elegidas.
  if (soloTocados ? enviar('collectionTagIds') : (Array.isArray(f.collectionTagIds) && f.collectionTagIds.length > 0)) {
    payload.tags = mergeTagsColeccion(f.tagsRaw || [], f.collectionTagIds || []).map((id) => ({ id }));
  }
  if (meta.length) payload.meta_data = meta;
  // Tanda 4 (PARTE B): renombrar un tono escribe attributes del padre; llega
  // explícito desde la ficha (no vive en el form) y solo viaja si se pasa.
  if (attributes) payload.attributes = attributes;
  // P3-12 «Más opciones»: featured / visibilidad / oferta(simple) / umbral(simple) / slug /
  // variación-por-defecto / reservas(simple) — SOLO lo que el tipo activó Y se tocó (enviar()).
  Object.assign(payload, payloadMasOpcionesProducto(f, { enviar, tipo: tipoActivo(tipos, f.productKind), esVariable: (payload.type === 'variable') }));

  // Precio/stock SOLO en simple (el variable se edita por variación).
  if (payload.type !== 'variable') {
    // Tanda 3c (defecto 1): NUNCA mandar precio/stock VACÍOS. `regular_price: ''`
    // o `stock_quantity: 0` derivado de un blanco PISAN el valor real en Woo (es
    // el mecanismo que dejó "precio — y 0 und"). Solo viajan si el form los tiene.
    // Un "0" EXPLÍCITO (agotado medido) sí viaja: has('0') es true, ≠ blanco.
    if (has(f.regularPrice)) payload.regular_price = num(f.regularPrice);
    if (has(f.salePrice)) {
      payload.sale_price = num(f.salePrice);
      if (has(f.saleFrom)) payload.date_on_sale_from = num(f.saleFrom);
      if (has(f.saleTo)) payload.date_on_sale_to = num(f.saleTo);
    }
    // Tanda 3c (ajuste 5): manage_stock SIEMPRE true (como backorders), sin control de UI.
    payload.manage_stock = true;
    if (has(f.stockQty)) payload.stock_quantity = parseInt(f.stockQty, 10) || 0;
  }

  return payload;
}

// Item 9: lo ÚNICO obligatorio para guardar en WooCommerce es nombre, precio y
// cantidad (y precio/cantidad solo en producto simple).
export function requiredToSaveWoo(form) {
  const f = form || {};
  const missing = [];
  if (!has(f.name)) missing.push('nombre');
  if ((f.type || 'simple') !== 'variable') {
    if (!has(f.regularPrice) || Number(f.regularPrice) <= 0) missing.push('precio');
    if (!has(f.stockQty)) missing.push('cantidad');
  }
  return missing;
}

// El aviso de publicar-incompleto vive en el EVALUADOR desde la tanda 6
// (utils/fichaEstado): el diálogo lista TODAS las recomendadas pendientes, no
// solo peso/imagen — publishNeedsConfirm se retiró con su suite.
